import React, { Component } from 'react';
import { Button, Form, Alert, Card, Container, Row, Col } from 'react-bootstrap';
import { GoPlus } from 'react-icons/go';
import WorkflowGraph, { addStep, removeStep, tailStepId, edgeFlowId } from './workflowGraph';
import StepProperties from './stepProperties';
import Dialog from '../../components/modal';
import WidgetStatus from '../../../common/widgetStatus';
import './workflows.css';

/**
 * WorkflowsSurface — cell-workflow template library editor.
 *
 * Layout: [ Library rail (188px) ][ Step graph editor (flex 1) ][ Properties panel (300px) ]
 *
 * Lifecycle:
 *   - On mount, loads templates via client.getTemplates().
 *   - Selecting a template shows its step graph.
 *   - Clicking a step node selects it and shows its properties.
 *   - The node toolbar adds a next step (flowing from it) or removes the step; dragging between handles
 *     wires a flow edge; "Add step" in the editor toolbar extends the flow even when nothing is selected.
 *   - Editing step properties immediately updates local state.
 *   - Save persists the entire cell_workflows list via client.saveWorkflows().
 *   - "New" creates a blank template.
 *
 * Storage shape: workflows is a LIST [{ name, start, nodes: [{id, ty, ...}], edges, defaults }, ...]
 * Each template carries its own name. nodes is a LIST [{ id, ty, ... }, ...].
 * Runtime indexes are built at use sites — never stored as source of truth.
 *
 * Props:
 *   store      — model store (not directly used; passed for consistency)
 *   client     — SystemOfWorkClient instance
 *   selection  — shell-level selection (not used by this surface; local state handles template+step)
 *   onSelect   — (item) => void  (not used by this surface)
 */
class WorkflowsSurface extends Component {
  constructor(props) {
    super(props);
    this.state = {
      /** The cell_workflows list: [{ name, start, nodes, edges, defaults }, ...] */
      workflows: [],
      /** Names of workspace templates (for workspace_init step select) */
      workspaceNames: [],
      /** Currently selected template name */
      selectedTemplate: null,
      /** Currently selected step id within the template */
      selectedStep: null,
      /** Currently selected flow edge { from, to } within the template (mutually exclusive with selectedStep) */
      selectedEdge: null,
      /** Whether an async save is in progress */
      saving: false,
      /** Save status: null | 'saved' | 'error' */
      saveStatus: null,
      loading: true,
      loadError: null,
      addWfModal: false,
      addWfName: '',
      addWfNameError: null,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    this._load();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async _load() {
    const { client } = this.props;
    if (!client) {
      this.setState({ loading: false });
      return;
    }
    this.setState({ loading: true, loadError: null });
    try {
      const { workflows, workspaces } = await client.getTemplates();
      if (!this._mounted) return;
      const wfList = workflows || [];
      const wsNames = (workspaces || []).map(ws => ws.name).filter(Boolean);
      this.setState({
        workflows: wfList,
        workspaceNames: wsNames,
        loading: false,
        selectedTemplate: wfList.length > 0 ? wfList[0].name : null,
        selectedStep: null,
        selectedEdge: null,
      });
    } catch (e) {
      if (!this._mounted) return;
      this.setState({ loading: false, loadError: 'Failed to load templates.' });
    }
  }

  /** Find a template object by name from the list. */
  _findTemplate(name) {
    return this.state.workflows.find(t => t.name === name) || null;
  }

  /** Find a step object by id from a template's nodes list. */
  _findStep(template, stepId) {
    if (!template || !stepId) return null;
    return (template.nodes || []).find(n => n.id === stepId) || null;
  }

  _selectTemplate(name) {
    this.setState({ selectedTemplate: name, selectedStep: null, selectedEdge: null, saveStatus: null });
  }

  // Selecting a step clears any edge selection (and vice versa) — only one is ever active. A null stepId
  // (e.g. a pane click) fully deselects.
  _selectStep(stepId) {
    this.setState({ selectedStep: stepId || null, selectedEdge: null });
  }

  _selectEdge(edge) {
    this.setState({ selectedStep: null, selectedEdge: edge || null });
  }

  _handleAddEdge(from, to) {
    const { workflows, selectedTemplate } = this.state;
    if (!selectedTemplate) return;
    const updatedWorkflows = workflows.map(t => {
      if (t.name !== selectedTemplate) return t;
      // Avoid duplicate edges
      const existingEdges = t.edges || [];
      const alreadyExists = existingEdges.some(e => e.from === from && e.to === to);
      if (alreadyExists) return t;
      return { ...t, edges: [...existingEdges, { from, to }] };
    });
    this.setState({ workflows: updatedWorkflows, saveStatus: null });
  }

  // Append a new worker_task step. When fromStepId is given (the node toolbar / a selected step), a flow
  // edge fromStepId → newStep is added too; the new step is selected and the template marked unsaved. The
  // pure reducer (addStep) handles id allocation, dedupe, and making the first step the entry (start).
  _handleAddStep(fromStepId) {
    const { workflows, selectedTemplate } = this.state;
    const template = this._findTemplate(selectedTemplate);
    if (!template) return;
    const { template: nextTemplate, newId } = addStep(template, fromStepId);
    const updatedWorkflows = workflows.map(t => (t.name === selectedTemplate ? nextTemplate : t));
    this.setState({
      workflows: updatedWorkflows,
      selectedStep: newId,
      selectedEdge: null,
      saveStatus: null,
    });
  }

  // The editor-toolbar "Add step" affordance — never a dead end. Extends from the selected step if any,
  // else from the flow tail, else (empty template) creates the first step.
  _handleAddStepFromToolbar() {
    const { selectedStep, selectedTemplate } = this.state;
    const template = this._findTemplate(selectedTemplate);
    if (!template) return;
    this._handleAddStep(selectedStep || tailStepId(template));
  }

  // Remove a step (and its incident flow edges). The pure reducer (removeStep) guards the last step and
  // reassigns `start` when the entry is removed; selection clears if the removed step was selected.
  _handleRemoveStep(stepId) {
    const { workflows, selectedTemplate, selectedStep } = this.state;
    const template = this._findTemplate(selectedTemplate);
    if (!template) return;
    const { template: nextTemplate, removed } = removeStep(template, stepId);
    if (!removed) return;
    const updatedWorkflows = workflows.map(t => (t.name === selectedTemplate ? nextTemplate : t));
    this.setState({
      workflows: updatedWorkflows,
      selectedStep: selectedStep === stepId ? null : selectedStep,
      selectedEdge: null,
      saveStatus: null,
    });
  }

  // Remove a flow edge by its (from, to) pair; clears the edge selection if it was the removed one.
  _handleRemoveEdge(from, to) {
    const { workflows, selectedTemplate, selectedEdge } = this.state;
    const template = this._findTemplate(selectedTemplate);
    if (!template) return;
    const existing = template.edges || [];
    const nextEdges = existing.filter(e => !(e.from === from && e.to === to));
    if (nextEdges.length === existing.length) return; // nothing removed
    const updatedWorkflows = workflows.map(t => (t.name === selectedTemplate ? { ...t, edges: nextEdges } : t));
    const clearSel = selectedEdge && selectedEdge.from === from && selectedEdge.to === to;
    this.setState({
      workflows: updatedWorkflows,
      selectedEdge: clearSel ? null : selectedEdge,
      saveStatus: null,
    });
  }

  _handleStepChange(stepId, updatedStep) {
    const { workflows, selectedTemplate } = this.state;
    if (!selectedTemplate) return;
    const updatedWorkflows = workflows.map(t => {
      if (t.name !== selectedTemplate) return t;
      const updatedNodes = (t.nodes || []).map(n => n.id === stepId ? { ...updatedStep, id: stepId } : n);
      return { ...t, nodes: updatedNodes };
    });
    this.setState({ workflows: updatedWorkflows, saveStatus: null });
  }

  _addNewTemplate() {
    this.setState({ addWfModal: true, addWfName: '', addWfNameError: null });
  }

  _submitNewTemplate() {
    const { addWfName, workflows } = this.state;
    if (!addWfName.trim()) return;
    const name = addWfName.trim().replace(/\s+/g, '_');
    if (workflows.some(t => t.name === name)) {
      this.setState({ addWfNameError: `Template "${name}" already exists.` });
      return;
    }
    const blank = {
      name,
      desc: '',
      start: 'step_1',
      nodes: [{ id: 'step_1', ty: 'worker_task' }],
      edges: []
    };
    this.setState({
      workflows: [...workflows, blank],
      selectedTemplate: name,
      selectedStep: 'step_1',
      selectedEdge: null,
      saveStatus: null,
      addWfModal: false,
    });
  }

  async _save() {
    const { client } = this.props;
    const { workflows } = this.state;
    if (!client) return;
    this.setState({ saving: true, saveStatus: null });
    try {
      await client.saveWorkflows(workflows);
      if (!this._mounted) return;
      this.setState({ saving: false, saveStatus: 'saved' });
    } catch (e) {
      if (!this._mounted) return;
      this.setState({ saving: false, saveStatus: 'error' });
    }
  }

  render() {
    const {
      workflows, workspaceNames, selectedTemplate, selectedStep, selectedEdge,
      loading, loadError, saving, saveStatus,
      addWfModal, addWfName, addWfNameError,
    } = this.state;

    if (loading) {
      return (
        <div className="mc-wf-container">
          <WidgetStatus variant="primary" loading>Loading…</WidgetStatus>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="mc-wf-container">
          <WidgetStatus variant="warning">{loadError}</WidgetStatus>
        </div>
      );
    }

    const template = selectedTemplate ? this._findTemplate(selectedTemplate) : null;
    const step = this._findStep(template, selectedStep);

    return (
      <Container fluid className="mc-wf-container">
        <Row className="mc-grid-row">
          {/* Library rail — panel Card identical to this surface's properties panel
              (mc-props-panel-card), so the left and right columns read as one surface. */}
          <Col xs="auto" className="d-flex flex-column mc-wf-rail-col">
            <Card className="mc-props-panel-card">
              <Card.Body className="mc-card-flush">
                <div className="mc-rail">
                  <Card.Text as="h6">Cell Workflows</Card.Text>
                  <hr />
                  <Button variant="light" size="sm" className="mc-rail-new" onClick={() => this._addNewTemplate()}>
                    <GoPlus /> New
                  </Button>

                  {workflows.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic', padding: '6px' }}>
                      No workflow templates yet. Click &ldquo;+ New&rdquo; to create one.
                    </p>
                  ) : (
                    workflows.map(t => (
                      <div
                        key={t.name}
                        className={'mc-rail-item' + (t.name === selectedTemplate ? ' on' : '')}
                        onClick={() => this._selectTemplate(t.name)}
                      >
                        <div className="mc-rail-item-name"><code>{t.name}</code></div>
                        {t.desc && (
                          <div className="mc-rail-item-desc">{t.desc}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Step-graph editor canvas — flat card, full-bleed */}
          <Col className="d-flex flex-column">
            <Card className="mc-wf-editor-card">
              <Card.Body className="mc-card-flush">
                <div className="mc-wf-editor">
                  {!selectedTemplate ? (
                    <div className="mc-wf-editor-empty">
                      Select or create a workflow template.
                    </div>
                  ) : (
                    <>
                      <div className="mc-wf-editor-toolbar">
                        <code className="mc-wf-editor-name">{selectedTemplate}</code>
                        <div style={{ flex: 1 }} />
                        {saveStatus === 'saved' && (
                          <span className="text-success" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Saved</span>
                        )}
                        {saveStatus === 'error' && (
                          <span className="text-danger" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Save failed</span>
                        )}
                        <Button variant="light" size="sm" onClick={() => this._handleAddStepFromToolbar()}>
                          <GoPlus /> Add step
                        </Button>
                        <Button variant="success" size="sm" disabled={saving} onClick={() => this._save()}>
                          {saving ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                      <div className="mc-wf-graph-wrap">
                        <WorkflowGraph
                          workflow={template}
                          selectedStep={selectedStep}
                          selectedEdgeId={selectedEdge ? edgeFlowId(selectedEdge) : null}
                          onSelectStep={(id) => this._selectStep(id)}
                          onSelectEdge={(edge) => this._selectEdge(edge)}
                          onAddEdge={(from, to) => this._handleAddEdge(from, to)}
                          onAddStep={(fromId) => this._handleAddStep(fromId)}
                          onRemoveStep={(id) => this._handleRemoveStep(id)}
                          onRemoveEdge={(from, to) => this._handleRemoveEdge(from, to)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Properties panel — flat card */}
          <Col sm={3} className="d-flex flex-column detail-panel-col">
            <Card className="mc-props-panel-card">
              <Card.Body className="mc-card-flush">
                <StepProperties
                  stepId={selectedStep}
                  step={step}
                  workspaceNames={workspaceNames}
                  onChange={(id, updated) => this._handleStepChange(id, updated)}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Dialog
          show={addWfModal}
          title="New Workflow Template"
          onHide={() => this.setState({ addWfModal: false })}
          onSubmit={() => this._submitNewTemplate()}
          submitLabel="Create"
          submitDisabled={!addWfName.trim()}
        >
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label><strong>Name</strong></Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={addWfName}
              onChange={e => this.setState({ addWfName: e.target.value, addWfNameError: null })}
              placeholder="e.g. my_workflow"
              autoFocus
            />
            {addWfNameError && (
              <Alert variant="warning" style={{ marginTop: 6, fontSize: '0.8rem', marginBottom: 0 }}>
                {addWfNameError}
              </Alert>
            )}
          </Form.Group>
        </Dialog>
      </Container>
    );
  }
}

export default WorkflowsSurface;
